import { useState, useMemo, useEffect } from "react";

import { Grid, Badge, Collapse } from "@mui/material";
import {
  FilterLinesIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Button,
  Typography,
} from "components";
import { gray } from "configs/colors";
import { i18n } from "translations";

interface FilterGridProps {
  children: React.ReactNode;
  action?: React.ReactNode;
  defaultOpen?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter: Record<string, any>;
  title?: string;
  showTitle?: boolean;
  showSubmit?: boolean;
  disableAutoCollapse?: boolean;
  onSubmit?: () => void;
}
interface FilterGridItemProps {
  children: React.ReactNode;
}

const FilterGrid = (props: FilterGridProps) => {
  const {
    children,
    action,
    defaultOpen = false,
    filter,
    showTitle = true,
    title = "",
    showSubmit = false,
    disableAutoCollapse = false,
    onSubmit,
  } = props;
  const [initDone, setInitDone] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultOpen);
  const totalFilterSelected = useMemo(() => {
    return Object.entries(filter).filter(
      ([key, value]) =>
        value !== "" &&
        value !== undefined &&
        value !== null &&
        value.length !== 0 &&
        !["page", "limit"]?.includes(key)
    ).length;
  }, [filter]);

  useEffect(() => {
    if (
      totalFilterSelected &&
      !initDone &&
      !collapsed &&
      !disableAutoCollapse
    ) {
      setCollapsed(true);
      setInitDone(true);
    }
  }, [collapsed, initDone, totalFilterSelected, disableAutoCollapse]);

  return (
    <>
      <Grid container display="flex" justifyContent="space-between" mb="12px">
        {showTitle && !title && (
          <Grid item xs="auto">
            {showTitle && (
              <Typography type="TextLG" fontWeight={600} flex="0 0 auto">
                {collapsed && i18n.t("filterElements")}
              </Typography>
            )}
          </Grid>
        )}
        {title && (
          <Typography type="TextLG" fontWeight={600} flex="0 0 auto">
            {title}
          </Typography>
        )}
        <Grid item xs>
          <Grid
            container
            columnGap="12px"
            display="flex"
            justifyContent="flex-end"
            width="100%"
          >
            <Grid item xs={12} md>
              {action}
            </Grid>
            <Grid item xs={12} md="auto">
              <Button
                onClick={() => setCollapsed(!collapsed)}
                variant="outlined"
                color="inherit"
                size="sm"
                sx={{
                  display: "flex",
                  flex: "0 0 auto",
                  // rowGap: 2,
                  background: gray[50],
                }}
                useTypography
                startIcon={
                  <Badge
                    variant="dot"
                    badgeContent={totalFilterSelected}
                    color="primary"
                  >
                    <FilterLinesIcon fontSize="small" />
                  </Badge>
                }
                endIcon={
                  collapsed ? (
                    <ChevronUpIcon fontSize="small" sx={{ color: gray[800] }} />
                  ) : (
                    <ChevronDownIcon
                      fontSize="small"
                      sx={{ color: gray[800] }}
                    />
                  )
                }
              >
                {i18n.t("filters")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Collapse in={collapsed}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={3}
        >
          <Grid
            container
            item
            xs={12}
            xl="auto"
            flex="1 !important"
            spacing={3}
          >
            {children}
          </Grid>
          {showSubmit && (
            <Grid item xs={12} display="flex" justifyContent="flex-end">
              <Button size="sm" onClick={() => onSubmit?.()} useTypography>
                {i18n.t("apply")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Collapse>
    </>
  );
};
const FilterGridItem = (props: FilterGridItemProps) => {
  const { children } = props;
  return (
    <Grid item xs={12} sm={6} lg={4}>
      {children}
    </Grid>
  );
};
export { FilterGrid, FilterGridItem };
